/**
 * Why are we disabling rules-of-hooks here?
 *
 * Well, we are using several hooks in this class, which is not allowed by this rule.
 * However, we are not using these hooks in a component, but in a utility class.
 * The important thing to remember is that these functions must be called on every render,
 * unconditionally (i.e. not in a conditional statement) and not in a loop.
 * This is exactly what we are doing here. We are only using the class to group the functions together
 * and give additional context to the functions.
 */

/* eslint-disable @eslint-react/rules-of-hooks */
import React from "react";

import type {
    SyncSettingsService,
    SyncSettingsTopicDefinitions,
    SyncSettingsTopicValueType,
} from "@framework/SyncSettingsService";
import { useSubscribedValueConditionally } from "@framework/SyncSettingsService";

import type { SettingsContext, ViewContext } from "./ModuleContext";

export enum SyncSettingKey {
    ENSEMBLE = "ENSEMBLE",
    DATE = "DATE",
    TIME_SERIES = "TIME_SERIES",
    SURFACE = "SURFACE",
    CAMERA_POSITION_MAP = "SUBSURFACE_MAP_CAMERA_POSITION",
    CAMERA_POSITION_INTERSECTION = "INTERSECTION_CAMERA_POSITION",
    WELLBORE = "WELLBORE",
    INTERSECTION = "INTERSECTION",
    VERTICAL_SCALE = "VERTICAL_SCALE",
    INPLACE_VOLUMES_FILTER = "INPLACE_VOLUMES_FILTER",
    INPLACE_VOLUMES_RESULT_NAME = "INPLACE_VOLUMES_RESULT_NAME",
    PARAMETER = "PARAMETER",
}

export const SyncSettingsMeta = {
    [SyncSettingKey.ENSEMBLE]: { name: "Ensemble", abbreviation: "ENS" },
    [SyncSettingKey.DATE]: { name: "Date", abbreviation: "DATE" },
    [SyncSettingKey.TIME_SERIES]: { name: "Time series", abbreviation: "TS" },
    [SyncSettingKey.SURFACE]: { name: "Surface", abbreviation: "SURF" },
    [SyncSettingKey.CAMERA_POSITION_MAP]: { name: "Camera Position Map", abbreviation: "CAM_POS_MAP" },
    [SyncSettingKey.WELLBORE]: { name: "Wellbore", abbreviation: "WELLBORE" },
    [SyncSettingKey.INTERSECTION]: { name: "Intersection", abbreviation: "INT" },
    [SyncSettingKey.CAMERA_POSITION_INTERSECTION]: {
        name: "Camera Position Intersection",
        abbreviation: "CAM",
    },
    [SyncSettingKey.VERTICAL_SCALE]: { name: "Vertical Scale", abbreviation: "VSCAL" },
    [SyncSettingKey.INPLACE_VOLUMES_FILTER]: { name: "Inplace Volumes Filter", abbreviation: "IVF" },
    [SyncSettingKey.INPLACE_VOLUMES_RESULT_NAME]: { name: "Inplace Volumes Result Name", abbreviation: "IVRN" },
    [SyncSettingKey.PARAMETER]: { name: "Parameter", abbreviation: "Param" },
};

export class SyncSettingsHelper {
    private _syncSettingsService: SyncSettingsService;
    private _moduleContext: SettingsContext<any> | ViewContext<any> | null;
    private _activeSyncedKeys: SyncSettingKey[];

    constructor(
        activeSyncedKeys: SyncSettingKey[],
        syncSettingsService: SyncSettingsService,
        moduleContext?: SettingsContext<any> | ViewContext<any>,
    ) {
        this._activeSyncedKeys = activeSyncedKeys;
        this._syncSettingsService = syncSettingsService;
        this._moduleContext = moduleContext ?? null;
    }

    isSynced(key: SyncSettingKey): boolean {
        return this._activeSyncedKeys.includes(key);
    }

    useValue<T extends keyof SyncSettingsTopicDefinitions>(
        key: SyncSettingKey,
        topic: T,
    ): SyncSettingsTopicDefinitions[T] | null {
        const isSyncActiveForKey = this._activeSyncedKeys.includes(key);
        return useSubscribedValueConditionally(
            topic,
            isSyncActiveForKey,
            this._syncSettingsService,
            this._moduleContext?.getInstanceIdString(),
        );
    }

    publishValue<T extends keyof SyncSettingsTopicDefinitions>(
        key: SyncSettingKey,
        topic: T,
        value: SyncSettingsTopicValueType<T>,
    ) {
        const isSyncActiveForKey = this._activeSyncedKeys.includes(key);
        if (isSyncActiveForKey) {
            this._syncSettingsService.publishValue(topic, value, this._moduleContext?.getInstanceIdString());
        }
    }
}

export type UseRefStableSyncSettingsHelperOptions = {
    syncSettingsService: SyncSettingsService;
    moduleContext: SettingsContext<any> | ViewContext<any>;
};

export function useRefStableSyncSettingsHelper(options: UseRefStableSyncSettingsHelperOptions): SyncSettingsHelper {
    const syncedSettingKeys = options.moduleContext.useSyncedSettingKeys();

    const syncHelper = React.useMemo(
        () => new SyncSettingsHelper(syncedSettingKeys, options.syncSettingsService, options.moduleContext),
        [syncedSettingKeys, options.syncSettingsService, options.moduleContext],
    );

    return syncHelper;
}
